import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LucideCheck } from '@lucide/angular';

export interface StepItem {
  label: string;
  description?: string;
}

/** Shows progress through a multi-step task — completed, current, upcoming. */
@Component({
  selector: 'ds-stepper',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet],
  template: `
    <div>
      <p class="status">
        Langkah <strong>{{ currentIndex() + 1 }}</strong> dari {{ steps().length }}:
        {{ currentLabel() }}
      </p>
      <ol class="steps">
        @for (step of steps(); track $index; let i = $index) {
          <li
            class="step"
            [class.step--done]="i < currentIndex()"
            [class.step--current]="i === currentIndex()"
            [attr.aria-current]="i === currentIndex() ? 'step' : null"
          >
            <span class="step__marker" aria-hidden="true">
              @if (i < currentIndex()) {
                <ng-container
                  [ngComponentOutlet]="checkIcon"
                  [ngComponentOutletInputs]="{ size: 20, strokeWidth: 3 }"
                />
              } @else {
                {{ i + 1 }}
              }
            </span>
            <span class="step__text">
              <span class="step__label">
                {{ step.label }}
                @if (i < currentIndex()) {
                  <span class="sr-only">(selesai)</span>
                }
              </span>
              @if (step.description) {
                <span class="step__desc">{{ step.description }}</span>
              }
            </span>
          </li>
        }
      </ol>
    </div>
  `,
  styles: `
    .status {
      margin: 0 0 var(--space-4);
      font-size: var(--text-md);
      color: var(--color-text-secondary);

      strong {
        color: var(--color-text);
      }
    }

    .steps {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0;
    }

    .step {
      position: relative;
      display: flex;
      align-items: flex-start;
      gap: var(--space-3);
      padding-bottom: var(--space-5);

      /* connector line */
      &:not(:last-child)::before {
        content: '';
        position: absolute;
        left: 19px;
        top: 40px;
        bottom: 4px;
        width: 3px;
        border-radius: 2px;
        background: var(--color-border);
      }

      &--done:not(:last-child)::before {
        background: var(--color-teal-300);
      }

      &:last-child {
        padding-bottom: 0;
      }
    }

    .step__marker {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 2.5px solid var(--color-border-input);
      background: var(--color-surface);
      color: var(--color-text-secondary);
      font-weight: var(--font-weight-bold);
      font-size: var(--text-sm);
    }

    .step--done .step__marker {
      background: var(--color-teal-600);
      border-color: var(--color-teal-600);
      color: var(--color-text-on-primary);
    }

    .step--current .step__marker {
      border-color: var(--color-primary);
      color: var(--color-primary);
      box-shadow: 0 0 0 4px var(--color-primary-tint);
    }

    .step__text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding-top: var(--space-2);
    }

    .step__label {
      font-weight: var(--font-weight-bold);
      color: var(--color-text-secondary);
    }

    .step--current .step__label,
    .step--done .step__label {
      color: var(--color-text);
    }

    .step__desc {
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      clip-path: inset(50%);
      overflow: hidden;
    }

    /* horizontal layout when there is room */
    @media (min-width: 720px) {
      .steps {
        flex-direction: row;
      }

      .step {
        flex: 1;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding-bottom: 0;
        padding-right: var(--space-2);

        &:not(:last-child)::before {
          left: calc(50% + 28px);
          right: calc(-50% + 28px);
          top: 19px;
          bottom: auto;
          width: auto;
          height: 3px;
        }
      }

      .step__text {
        align-items: center;
      }
    }
  `,
})
export class StepperComponent {
  steps = input.required<StepItem[]>();
  currentIndex = input(0);

  protected readonly checkIcon = LucideCheck;
  protected readonly currentLabel = computed(
    () => this.steps()[this.currentIndex()]?.label ?? '',
  );
}
